import { AjaxContext, AppContext } from 'commons-ui';
import { useContext, useCallback } from 'react';
import _get from 'lodash/get';
import _isNil from 'lodash/isNil';
import dayjs from 'dayjs';
import { PAGE_NAMES, USER_PROFILE_RESOURCES, ACTION } from 'constant';
import usePermissionV2 from 'commons-ui/src/permission/usePermission';

import { UserDetailsContext } from 'context/userDetails';
import {
	IFormattedKycData,
	IKycDocumentFields,
	KYC_STATUS_VALUE,
	MANUAL_VERIFICATION_REQ_LABEL,
	MANUAL_VERIFICATION_REQ_VALUE_OPTIONS_MAP,
	VIEW_FIELD_KEYS
} from './constants';

const useService = () => {
	const {
		app: { appName }
	} = useContext(AppContext);
	const { postData } = useContext(AjaxContext);
	const { hasPermission: hasPermissionV2 } = usePermissionV2();
	const {
		selectedUser: { uid }
	} = useContext(UserDetailsContext);

	const getFullName = (source: IKycDocumentFields): string => {
		let fullName = _get(source, VIEW_FIELD_KEYS.FULL_NAME);
		const firstName = _get(source, VIEW_FIELD_KEYS.FIRST_NAME);
		const lastName = _get(source, VIEW_FIELD_KEYS.LAST_NAME);
		if (_isNil(fullName)) {
			if (!_isNil(firstName) && !_isNil(lastName)) {
				fullName = `${firstName} ${lastName}`;
			} else {
				fullName = firstName || lastName;
			}
		}
		return fullName;
	};

	const getRejectionMessage = (source: IKycDocumentFields): string => {
		let rejectionMessage = _get(source, VIEW_FIELD_KEYS.EXTERNAL_MESSAGE);
		const message = _get(source, VIEW_FIELD_KEYS.REJECTION_MESSAGE);
		if (_isNil(rejectionMessage)) {
			if (!_isNil(message)) {
				rejectionMessage = message;
			} else {
				rejectionMessage = 'NA';
			}
		}
		if (source?.status === 'APPROVED') {
			rejectionMessage = null;
		}
		return rejectionMessage;
	};

	const formatData = (source: IKycDocumentFields): IFormattedKycData => ({
		[VIEW_FIELD_KEYS.KYC_STATUS]: _get(source, VIEW_FIELD_KEYS.KYC_STATUS) || KYC_STATUS_VALUE.NOT_INITIATED,
		[VIEW_FIELD_KEYS.KYC_ID]: _get(source, VIEW_FIELD_KEYS.KYC_ID),
		[VIEW_FIELD_KEYS.MANUAL_VERIFICATION_REQ]: _get(
			MANUAL_VERIFICATION_REQ_LABEL,
			_get(MANUAL_VERIFICATION_REQ_VALUE_OPTIONS_MAP, _get(source, VIEW_FIELD_KEYS.MANUAL_VERIFICATION_REQ))
		),
		[VIEW_FIELD_KEYS.APPROVED_BY]: _get(source, VIEW_FIELD_KEYS.APPROVED_BY),
		[VIEW_FIELD_KEYS.KYC_DETAILS]: {
			[VIEW_FIELD_KEYS.DATE_OF_BIRTH]: _get(source, VIEW_FIELD_KEYS.DATE_OF_BIRTH),
			[VIEW_FIELD_KEYS.DOC_NUMBER]: _get(source, VIEW_FIELD_KEYS.DOC_NUMBER),
			[VIEW_FIELD_KEYS.DOC_IMAGE]: _get(source, VIEW_FIELD_KEYS.DOC_IMAGE),
			[VIEW_FIELD_KEYS.DOC_TYPE]: _get(source, VIEW_FIELD_KEYS.DOC_TYPE),
			[VIEW_FIELD_KEYS.REJECTION_MESSAGE]:
				_get(source, VIEW_FIELD_KEYS.EXTERNAL_MESSAGE) || _get(source, VIEW_FIELD_KEYS.REJECTION_MESSAGE),
			[VIEW_FIELD_KEYS.FIRST_NAME]: _get(source, VIEW_FIELD_KEYS.FIRST_NAME),
			[VIEW_FIELD_KEYS.LAST_NAME]: _get(source, VIEW_FIELD_KEYS.LAST_NAME),
			[VIEW_FIELD_KEYS.FULL_NAME]: getFullName(source),
			[VIEW_FIELD_KEYS.REJECTION_MESSAGE]: getRejectionMessage(source),
			[VIEW_FIELD_KEYS.TIMESTAMP]: _get(source, VIEW_FIELD_KEYS.TIMESTAMP)
				? dayjs(new Date(_get(source, VIEW_FIELD_KEYS.TIMESTAMP))).format('DD-MM-YYYY hh:mm A')
				: null,
			[VIEW_FIELD_KEYS.LAST_UPDATED_AT]: _get(source, VIEW_FIELD_KEYS.LAST_UPDATED_AT)
				? dayjs(new Date(_get(source, VIEW_FIELD_KEYS.LAST_UPDATED_AT))).format('DD-MM-YYYY hh:mm A')
				: null,
			[VIEW_FIELD_KEYS.UID]: _get(source, VIEW_FIELD_KEYS.UID),
			[VIEW_FIELD_KEYS.OCR_RESULT]: {
				..._get(source, VIEW_FIELD_KEYS.OCR_RESULT),
				..._get(source, [VIEW_FIELD_KEYS.OCR_RESULT, VIEW_FIELD_KEYS.OCR_RESULT_MSG])
			},
			[VIEW_FIELD_KEYS.DEVICE_TYPE]: _get(source, VIEW_FIELD_KEYS.DEVICE_TYPE)
		}
	});

	const getKycDetails = useCallback(async () => {
		const result = await postData({
			endpoint: `/dashboard/getKycDetails/${PAGE_NAMES.userProfile}-${USER_PROFILE_RESOURCES.kycDetails}-getKycDetails`,
			queryParams: { appName },
			body: { uid }
		});

		const data = _get(result, ['data', 'data'], {});
		const kycData: IKycDocumentFields = { ...data.kycData };
		const formattedKycData = formatData(kycData);
		return formattedKycData;
	}, []);

	const getTdsDocData = useCallback(async () => {
		const result = await postData({
			endpoint: `/dashboard/getKycDetails/${PAGE_NAMES.userProfile}-${USER_PROFILE_RESOURCES.kycDetails}-getKycDetails`,
			queryParams: { appName },
			body: { uid }
		});

		const data = _get(result, ['data', 'data'], {});
		const tdsDocData: IKycDocumentFields = { ...data.tdsDocData };
		return formatData(tdsDocData);
	}, []);

	const manuallyLinkPan = useCallback(async (panObjectId: number) => {
		const result = await postData({
			endpoint: `/dashboard/manuallyLinkPan/${PAGE_NAMES.userProfile}-${USER_PROFILE_RESOURCES.kycDetails}-manuallyLinkPan`,
			queryParams: { appName },
			body: { uid, panObjectId }
		});
		return result;
	}, []);

	const canApprovePan = hasPermissionV2({
		configPageName: PAGE_NAMES.userProfile,
		configResourceName: USER_PROFILE_RESOURCES.kycDetails,
		configActionName: ACTION.manuallyLinkPan
	});

	return { getKycDetails, getTdsDocData, manuallyLinkPan, canApprovePan };
};

export default useService;
